import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'

const THEME_STYLES = {
  light: {
    container: 'bg-white',
    text: 'text-gray-900',
    subtext: 'text-gray-600',
    card: 'bg-gray-50 border-gray-200',
    link: 'text-blue-600'
  },
  dark: {
    container: 'bg-gray-900',
    text: 'text-white',
    subtext: 'text-gray-400',
    card: 'bg-gray-800 border-gray-700',
    link: 'text-blue-400'
  },
  brand: {
    container: '',
    text: '',
    subtext: '',
    card: '',
    link: ''
  }
}

function BioPagePreview({
  title,
  description,
  theme = 'light',
  products = [],
  logoUrl,
  colors = {},
  shopUrl,
  impressumUrl,
  datenschutzUrl
}) {
  const { t } = useTranslation('ecommerce')
  const styles = THEME_STYLES[theme] || THEME_STYLES.light

  const isBrandTheme = theme === 'brand'
  const bgColor = isBrandTheme ? colors.background : undefined
  const primaryColor = isBrandTheme ? colors.primary : undefined
  const textColor = isBrandTheme ? colors.primary : undefined

  const hasLegalLinks = impressumUrl || datenschutzUrl

  return (
    <div
      data-testid="mobile-frame"
      className="relative mx-auto border-4 border-gray-800 rounded-[2.5rem] overflow-hidden shadow-xl"
      style={{ width: '280px', height: '560px' }}
    >
      {/* Phone notch */}
      <div className="absolute top-0 left-1/2 transform -translate-x-1/2 w-24 h-6 bg-gray-800 rounded-b-xl z-10" />

      {/* Screen content */}
      <div
        data-testid="bio-page-preview"
        className={`h-full overflow-y-auto pt-8 pb-4 px-4 ${!isBrandTheme ? styles.container : ''}`}
        style={isBrandTheme ? { backgroundColor: bgColor } : undefined}
      >
        {/* Header */}
        <div className="text-center mb-6">
          {logoUrl && (
            <img
              src={logoUrl}
              alt="Logo"
              className="w-16 h-16 mx-auto rounded-full object-cover mb-3"
            />
          )}
          <h1
            className={`text-lg font-bold ${!isBrandTheme ? styles.text : ''}`}
            style={isBrandTheme ? { color: textColor } : undefined}
          >
            {title}
          </h1>
          {description && (
            <p
              className={`text-sm mt-1 ${!isBrandTheme ? styles.subtext : ''}`}
              style={isBrandTheme ? { color: textColor, opacity: 0.8 } : undefined}
            >
              {description}
            </p>
          )}
        </div>

        {/* Shop Link Button */}
        {shopUrl && (
          <div className="mb-4">
            <a
              href={shopUrl}
              target="_blank"
              rel="noopener noreferrer"
              className={`block w-full py-3 px-4 rounded-lg text-center text-sm font-medium transition-colors ${
                isBrandTheme
                  ? 'text-white'
                  : 'bg-primary-600 text-white hover:bg-primary-700'
              }`}
              style={
                isBrandTheme
                  ? { backgroundColor: primaryColor, color: '#ffffff' }
                  : undefined
              }
            >
              {t('bioPageBuilder.preview.visitShop', 'Visit Shop')}
            </a>
          </div>
        )}

        {/* Products */}
        {products.length === 0 ? (
          <div
            className={`text-center py-8 text-sm ${!isBrandTheme ? styles.subtext : ''}`}
            style={isBrandTheme ? { color: textColor, opacity: 0.6 } : undefined}
          >
            {t('bioPageBuilder.preview.noProducts', 'No products to display')}
          </div>
        ) : (
          <div className="space-y-3">
            {products.map((product) => (
              <div
                key={product.product_id}
                className={`flex items-center gap-3 p-3 rounded-lg border ${!isBrandTheme ? styles.card : 'border-opacity-20'}`}
                style={
                  isBrandTheme
                    ? {
                        backgroundColor: `${primaryColor}10`,
                        borderColor: primaryColor
                      }
                    : undefined
                }
              >
                {product.image_url && (
                  <img
                    src={product.image_url}
                    alt={product.title}
                    className="w-12 h-12 object-cover rounded"
                  />
                )}
                <div className="flex-1 min-w-0">
                  <div
                    className={`text-sm font-medium truncate ${!isBrandTheme ? styles.text : ''}`}
                    style={isBrandTheme ? { color: textColor } : undefined}
                  >
                    {product.title}
                  </div>
                  <div
                    className={`text-sm ${!isBrandTheme ? styles.subtext : ''}`}
                    style={isBrandTheme ? { color: primaryColor } : undefined}
                  >
                    €{product.price?.toFixed(2)}
                  </div>
                </div>
                <svg
                  className={`w-5 h-5 ${!isBrandTheme ? styles.subtext : ''}`}
                  style={isBrandTheme ? { color: primaryColor } : undefined}
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 5l7 7-7 7"
                  />
                </svg>
              </div>
            ))}
          </div>
        )}

        {/* Footer with Legal Links */}
        <div className="mt-6 pt-4 border-t border-gray-200 border-opacity-20">
          {/* Legal Links */}
          {hasLegalLinks && (
            <div className="flex justify-center gap-3 mb-3 text-xs">
              {impressumUrl && (
                <a
                  href={impressumUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`hover:underline ${!isBrandTheme ? styles.link : ''}`}
                  style={isBrandTheme ? { color: primaryColor } : undefined}
                >
                  {t('bioPageBuilder.preview.impressum', 'Impressum')}
                </a>
              )}
              {impressumUrl && datenschutzUrl && (
                <span
                  className={!isBrandTheme ? styles.subtext : ''}
                  style={isBrandTheme ? { color: textColor, opacity: 0.5 } : undefined}
                >
                  •
                </span>
              )}
              {datenschutzUrl && (
                <a
                  href={datenschutzUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`hover:underline ${!isBrandTheme ? styles.link : ''}`}
                  style={isBrandTheme ? { color: primaryColor } : undefined}
                >
                  {t('bioPageBuilder.preview.privacy', 'Privacy')}
                </a>
              )}
            </div>
          )}

          {/* Powered by */}
          <div className="text-center">
            <span
              className={`text-xs ${!isBrandTheme ? styles.subtext : ''}`}
              style={isBrandTheme ? { color: textColor, opacity: 0.5 } : undefined}
            >
              Powered by StoryPlannerPro
            </span>
          </div>
        </div>
      </div>
    </div>
  )
}

BioPagePreview.propTypes = {
  title: PropTypes.string.isRequired,
  description: PropTypes.string,
  theme: PropTypes.oneOf(['light', 'dark', 'brand']),
  products: PropTypes.arrayOf(PropTypes.shape({
    product_id: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
    price: PropTypes.number,
    image_url: PropTypes.string,
  })),
  logoUrl: PropTypes.string,
  colors: PropTypes.shape({
    primary: PropTypes.string,
    background: PropTypes.string,
  }),
  shopUrl: PropTypes.string,
  impressumUrl: PropTypes.string,
  datenschutzUrl: PropTypes.string,
}

export default BioPagePreview
